import { AI_LEVELS, type Color, getVariant } from '@chessbut/engine';
import { Flag, RotateCcw, Share2, Undo2, Zap } from 'lucide-react';
import { useEffect, useState } from 'react';
import { GameView, verdictFor } from '../components/GameView.tsx';
import { LocalGame, type LocalMode, saveKeyFor, useLocalGame } from '../game/localGame.ts';
import { newRef, submitResult } from '../game/uploads.ts';
import { canOpenVariant, useSession } from '../lib/session.ts';
import { share } from '../lib/share.ts';
import { dayUrl, resultShareText } from '../lib/shareText.ts';
import { client } from '../online/client.ts';
import { NotFound } from './Static.tsx';

export function botName(variantId: string, level: number): string {
  const v = getVariant(variantId);
  return v?.info.bots?.[level - 1] ?? AI_LEVELS.find((l) => l.id === level)?.name ?? 'Computer';
}

export function LocalGameScreen({ variantId, mode }: { variantId: string; mode: Exclude<LocalMode, 'challenge'> }) {
  const variant = getVariant(variantId);
  const session = useSession();
  if (!variant || !canOpenVariant(session, variantId)) return <NotFound />;
  return <LocalGameInner key={`${variantId}-${mode}`} variantId={variantId} mode={mode} />;
}

function LocalGameInner({ variantId, mode }: { variantId: string; mode: Exclude<LocalMode, 'challenge'> }) {
  const key = saveKeyFor({ variant: variantId, mode });
  const [lg, setLg] = useState(() => LocalGame.restore(key) ?? new LocalGame({ variant: variantId, mode, human: 0, level: 2 }));
  const snap = useLocalGame(lg);
  const { me } = useSession();
  const [confirmResign, setConfirmResign] = useState(false);

  useEffect(() => {
    lg.resume();
    return () => lg.dispose();
  }, [lg]);

  useEffect(() => {
    if (!confirmResign) return;
    const t = setTimeout(() => setConfirmResign(false), 3000);
    return () => clearTimeout(t);
  }, [confirmResign]);

  const status = lg.status();
  // Report finished bot games (unrated: analytics, history, streaks). The upload queue keeps
  // retrying until the server has it.
  useEffect(() => {
    if (!status.over || mode !== 'bot' || lg.reported || status.reason === 'aborted' || lg.game.ply === 0) return;
    lg.markReported();
    submitResult('/api/bot-games', {
      ref: newRef(),
      variant: variantId,
      level: lg.config.level,
      color: lg.config.human === 0 ? 'white' : 'black',
      moves: lg.game.history.map((r) => r.uci),
      result: status.result,
      reason: status.reason,
      durationMs: Date.now() - lg.startedAt,
    }).catch(() => {});
  }, [status.over, snap.version]);

  const vsBot = mode === 'bot';
  const human = lg.config.human;
  const bot = botName(variantId, lg.config.level);
  const you = { name: me?.name ?? 'You', username: me?.username ?? null, isYou: true, streak: me?.streak.current || undefined };
  const botLabel = { name: bot, rating: `level ${lg.config.level}` };
  const white = vsBot ? (human === 0 ? you : botLabel) : { name: 'White' };
  const black = vsBot ? (human === 1 ? you : botLabel) : { name: 'Black' };
  const variant = lg.game.variant;
  const outcome = status.winner === undefined ? 'draw' : status.winner === human ? 'win' : 'loss';

  const newGame = (swap = false) => {
    LocalGame.clear(key);
    const c = lg.config;
    setLg(new LocalGame({ ...c, human: (swap ? 1 - c.human : c.human) as Color }));
  };
  const shareText = vsBot && status.over ? resultShareText({ variant, outcome, plies: lg.game.ply, vs: bot, streak: me?.streak.current }) : undefined;

  return (
    <GameView
      game={lg.game}
      version={snap.version}
      white={white}
      black={black}
      perspective={vsBot ? human : 0}
      you={vsBot ? human : null}
      movable={(c) => lg.canMove(c)}
      status={status}
      thinking={snap.thinking ? `${bot} is thinking…` : null}
      onMove={(uci) => lg.move(uci)}
      mode={vsBot ? `vs computer · level ${lg.config.level}` : 'pass & play'}
      actions={
        <>
          {!status.over && (
            <button className="btn" disabled={!lg.canUndo()} onClick={() => lg.undo()}>
              <Undo2 className="icon" />
              Undo
            </button>
          )}
          {!status.over ? (
            <button
              className={`btn ${confirmResign ? 'danger' : ''}`}
              onClick={() => {
                if (confirmResign) lg.resign();
                else setConfirmResign(true);
              }}
            >
              <Flag className="icon" />
              {confirmResign ? 'Sure?' : 'Resign'}
            </button>
          ) : (
            <button className="btn" onClick={() => newGame(true)}>
              <RotateCcw className="icon" />
              Again
            </button>
          )}
          {vsBot && (
            <button className="btn" onClick={() => client.quickMatch(variantId)}>
              <Zap className="icon" />
              Online
            </button>
          )}
        </>
      }
      end={
        status.over
          ? {
              verdict: verdictFor(status, vsBot ? human : null),
              share: shareText,
              extra: vsBot ? <p className="end-note">Bot games are unrated. Play online for rating and daily points.</p> : null,
              actions: (
                <>
                  {shareText && (
                    <button className="btn primary big block" onClick={() => share({ kind: 'bot', text: shareText, url: dayUrl(variant) })}>
                      <Share2 className="icon" />
                      Share result
                    </button>
                  )}
                  <button className="btn block" onClick={() => newGame(true)}>
                    {vsBot ? 'Rematch (swap colours)' : 'Play again'}
                  </button>
                  {vsBot && (
                    <button className="btn block" onClick={() => client.quickMatch(variantId)}>
                      Play someone online
                    </button>
                  )}
                </>
              ),
            }
          : null
      }
    />
  );
}
